import { useContext, useState } from 'react';
import { useConsultationSummary } from '../../../../../hooks/fetchers/useConsultationSummary/useConsultationSummary';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import Button from '../../../../designSystem/Button/Button';
import {
  ButtonAlignment,
  ButtonColor,
  ButtonSize,
} from '../../../../designSystem/Button/Button.types';
import { useMutation, useQueryClient } from 'react-query';
import { CreateConsultForthStepMutation } from './CreateConsultForthStep.types';
import {
  CONFIRM_CONSULTATION_QUERY_KEY,
  downloadAttachment,
  queryConfirmConsultation,
} from './CreateConsultForthStep.query';
import Modal from '../../../../commons/Modal/Modal';
import CreateConsultPaymentModal from './CreateConsultPaymentModal/CreateConsultPaymentModal';
import { StepWizardContext } from '../../../../commons/StepWizard/StepWizard.context';
import { useDomainsList } from '../../../../../hooks/fetchers/useDomainsList/useDomainsList';
import { useMergedStatesFromFetchers } from '../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Loading from '../../../../commons/Loading/Loading';

export default () => {
  const { consultationId } = useParams();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: domainsList, isLoading: isDomainsLoading } = useDomainsList();

  const { setCurrentStep } = useContext(StepWizardContext);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);

  const amount = 30.0;

  const { data, isLoading: isConsultationSummaryLoading } = useConsultationSummary(parseInt(consultationId as string), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(consultationId),
  });

  const { mutate, isLoading: isConfirmLoading } = useMutation<Awaited<ReturnType<CreateConsultForthStepMutation>>, Error>(
    () => queryConfirmConsultation(parseInt(consultationId as string)),
    {
      mutationKey: CONFIRM_CONSULTATION_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: () => {
        setModalOpen(true);
      },
      onError: (error) => {
        if (error.name === 'No company found' || error.name === 'Not enough company found') {
          setPaymentModalOpen(true);
        } else {
          alert(error.message);
        }
      },
    }
  );

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isDomainsLoading, isConsultationSummaryLoading],
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <Helmet>
        <title>Étape 4 - Nouvelle consultation</title>
      </Helmet>
      <div style={{ display: 'flex', flex: 1, alignSelf: 'normal' }}>
        <div className="createConsultForthStep">
          <div className="createConsultForthStep__item">
            <div className="createConsultForthStep__item-row">
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">
                  Titre de la consultation
                </label>
                <span className="createConsultForthStep__item-text-3">{data?.infos.title}</span>
              </div>
              {data?.infos.ref && (
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">Référence</label>
                  <span className="createConsultForthStep__item-text-2">{data?.infos.ref}</span>
                </div>
              )}
            </div>
            <div className="createConsultForthStep__item-row">
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">Type de prestation</label>
                <span className="createConsultForthStep__item-text-2">{domainsList?.find(({ id }) => id == data?.infos.type)?.name || data?.infos.type}</span>
              </div>
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">Domaine d'activité</label>
                <span className="createConsultForthStep__item-text-2">
                  {data?.infos.fieldOfActivity}
                </span>
              </div>
            </div>
          </div>
          <div className="createConsultForthStep__item createConsultForthStep__item--right">
            <div className="createConsultForthStep__item-row">
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">Début de l'offre</label>
                <span className="createConsultForthStep__item-text-4">
                  {moment(data?.infos.start).format('dddd DD MMMM YYYY')}
                </span>
              </div>
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">
                  Durée de la consultation
                </label>
                <span className="createConsultForthStep__item-text-2">
                  {moment(data?.infos.end).diff(data?.infos.start, 'days')} jours
                </span>
              </div>
            </div>
            <div className="createConsultForthStep__item-row">
              <div className="createConsultForthStep__item-content">
                <label className="createConsultForthStep__item-text-1">Fin de l'offre</label>
                <span className="createConsultForthStep__item-text-4">
                  {moment(data?.infos.end).format('dddd DD MMMM YYYY')}
                </span>
              </div>
            </div>
          </div>
          {Boolean(data && data?.prestations.length > 0) && (
            <div className="createConsultForthStep__item">
              <div className="createConsultForthStep__item-row">
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">
                    Contenu de la consultation
                  </label>
                  {data?.prestations.map(({ quantity, description }, index) => (
                    <span className="createConsultForthStep__item-text-2" key={index}>
                      {index === 0 && `\n`}{' '}
                      {quantity && (
                        <>
                          <strong>{quantity}</strong>x
                        </>
                      )}{' '}
                      {description} {`\n\n`}
                    </span>
                  ))}
                  {data?.infos.further_description &&
                    <span className="createConsultForthStep__item-text-2">
                      {data.infos.further_description}
                    </span>
                  }
                </div>
              </div>
            </div>
          )}
          {Boolean(data?.infos.firstName) && (
            <div className="createConsultForthStep__item createConsultForthStep__item--right">
              <label className="createConsultForthStep__item-text-5">Personne à contacter</label>
              <div className="createConsultForthStep__item-row">
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">Nom</label>
                  <span className="createConsultForthStep__item-text-2">
                    {data?.infos.lastName}
                  </span>
                </div>
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">Prénom</label>
                  <span className="createConsultForthStep__item-text-2">
                    {data?.infos.firstName}
                  </span>
                </div>
              </div>
              <div className="createConsultForthStep__item-row">
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">Adresse email</label>
                  <span className="createConsultForthStep__item-text-2">{data?.infos.email}</span>
                </div>
                <div className="createConsultForthStep__item-content">
                  <label className="createConsultForthStep__item-text-1">Numéro de téléphone</label>
                  <span className="createConsultForthStep__item-text-2">{data?.infos.phone}</span>
                </div>
              </div>
            </div>
          )}
          {data?.infos.attachments &&
          <div className="createConsultForthStep__item" style={{ paddingTop: 0 }}>
          {data.infos.attachments.split(',').map((file, index) => (
            <Button
              key={index}
              style={{
                alignSelf: 'unset',
                textAlign: 'center',
                borderRadius: 6,
                marginBottom: 'var(--spacing-s)',
                width: '100%',
                paddingLeft: 0,
                paddingRight: 0,
              }}
              onClick={(e) => {
                e.preventDefault()
                downloadAttachment(file)
              }}
            >
              {`Pièce jointe n°${index + 1}`}
            </Button>
          ))}
        </div>
          }
        </div>
      </div>
      <Button
        style={{
          alignSelf: 'unset',
          textAlign: 'center',
          borderRadius: 6,
          marginBottom: 'var(--spacing-s)',
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
        }}
        color={ButtonColor.Contrast}
        onClick={(e) => {
          e.preventDefault();
          navigate('/dashboard/consultations');
        }}>
        Continuer plus tard
      </Button>
      <Button
        style={{
          alignSelf: 'unset',
          textAlign: 'center',
          borderRadius: 6,
          marginBottom: 'var(--spacing-s)',
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
        }}
        onClick={(e) => {
          e.preventDefault();
          setCurrentStep(3);
        }}>
        Précedent
      </Button>
      <Button
        style={{
          alignSelf: 'unset',
          textAlign: 'center',
          borderRadius: 6,
          marginBottom: 'var(--spacing-sToM)',
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
        }}
        onClick={(e) => {
          e.preventDefault();
          mutate();
          // Disabled because the PUBs shouldn't be able to pay directly
          // if (data?.infos.is_premium === 0) {
          //   setPaymentModalOpen(true);
          // } else {
          //   mutate();
          // }
        }}
        isLoading={isConfirmLoading}
        size={ButtonSize.Large}
        disabled={false}>
        Confirmer
      </Button>
      <Modal isOpen={isModalOpen} setOpen={setModalOpen}>
        <h2>Confirmation</h2>
        <p>Votre consultation a été déposée avec succès.</p>
        <Button
          className="stepLayerConsultationNew__modalButton"
          size={ButtonSize.Large}
          alignment={ButtonAlignment.Center}
          onClick={() => navigate('/dashboard/consultations')}>
          Voir mes consultations
        </Button>
      </Modal>
      {consultationId && isPaymentModalOpen && (
        <CreateConsultPaymentModal
          isOpen={isPaymentModalOpen}
          setOpen={setPaymentModalOpen}
          closeOnClickOutside
          paymentData={{
            consultationId: parseInt(consultationId),
            amount: amount || 0,
          }}
        />
      )}
    </>
  );
};
